import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';
import {ChartsService} from '../../charts.service';
import {timeout} from 'rxjs/operators';
import {ColorThemeService} from '../../../common/services/color-theme.service';
import {ThemeColor} from '../../../common/enums/theme-color.enum';
import {PaymentWindowService} from '../../../payment-window.service';

declare let Plotly: any;

@Component({
  selector: 'iv-live-skew-surface',
  templateUrl: './skew-surface.component.html',
  styleUrls: ['./skew-surface.component.scss']
})
export class SkewSurfaceComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    private optionMonitorService: ChartsService,
    private colorThemeService: ColorThemeService,

    protected readonly paymentWindowService: PaymentWindowService
  ) {
    this.colorThemeSubscription = this.colorThemeService.$colorTheme
      .subscribe((colorTheme: ThemeColor) => {
        this.colorTheme = colorTheme;
        this._drawPlot();
      });
  }

  @Input() instrument;
  @Input() tabType: string;

  private subscription: Subscription;
  private colorThemeSubscription: Subscription;

  chartId: string = 'id_' + Math.random().toString(16).slice(2);

  type = 'moneyness';

  surfaceData = {
    x: [],
    y: [],
    z: []
  };
  tableData = [];
  tableDataSize = 0;
  periods = [];
  values = [];
  private _timer = null;
  private colorTheme: ThemeColor;

  private _mode = null;

  get mode() {
    return this._mode;
  }

  @Input() set mode(value) {
    this._mode = value;
    this.isEOD = (value === 'eod_futures');
  }

  private _isEOD = true;

  get isEOD(): boolean {
    return this._isEOD;
  }

  set isEOD(value: boolean) {
    this._isEOD = value;
  }

  ngOnInit() {
    this.paymentWindowService.showPaymentWindow();
    this.colorTheme = this.colorThemeService.getColorTheme();
    this._timer = setInterval(() => {
      if (!this.isEOD) {
        this.initSkewSurface();
      }
    }, 10 * 1000);
    this.switchMode(); // временно
  }

  ngAfterViewInit() {
    this.initSkewSurface();
  }

  ngOnDestroy(): void {
    if (this._timer != null) {
      clearInterval(this._timer);
    }
    this.subscription.unsubscribe();
    this.colorThemeSubscription.unsubscribe();
  }

  initSkewSurface() {
    this._loadData(this.type).then(data => {
      this._fillData(data);
      this._drawPlot();
    });
  }

  fill(N, func) {
    const empty = Array.apply(null, Array(N));
    return empty.map(func);
  }

  changeSurfaceType(type: string) {
    this.type = type;
    this._changeSurface();
  }

  switchMode() {
    this.isEOD = !this.isEOD;
    this._loadData(this.type).then(data => {
      this._fillData(data);
      this._drawPlot();
    });
  }

  private _drawPlot() {
    const trace = {
      x: this.surfaceData.x,
      y: this.surfaceData.y,
      z: this.surfaceData.z,
      type: 'surface',
      uid: '29f03f',
      showscale: false
    };

    const data = [trace];
    const axisTextColor = this.colorTheme === ThemeColor.DARK ? '#fff' : '#000';

    const surfaceData = {
      autosize: true,
      width: 1100,
      height: 300,
      showlegend: false,
      margin: {
        l: 0,
        r: 0,
        b: 0,
        t: 0
      },
      autosizable: true,
      scene: {
        xaxis: {
          type: 'linear',
          title: 'Maturity (days)',
          color: axisTextColor
        },
        yaxis: {
          type: 'linear',
          title: (this.type.charAt(0).toUpperCase() + this.type.slice(1)),
          color: axisTextColor
        },
        zaxis: {
          type: 'linear',
          title: 'Volatility (%)',
          color: axisTextColor
        },
        camera: {
          up: {
            x: 0,
            y: 0,
            z: 2
          },
          center: {
            x: 0.2,
            y: 0.2,
            z: 0
          },
          eye: {
            x: 1.25,
            y: 1.25,
            z: 1.25
          }
        }
      }
    };
    Plotly.purge('ivSurface');
    console.log(data,surfaceData,axisTextColor)
    Plotly.newPlot('ivSurface', data, surfaceData);
  }

  private _changeSurface() {
    this._loadData(this.type).then(data => {
      this._fillData(data);
      this._drawPlot();
    });
  }

  private _loadData(type) {
    return new Promise((resolve, reject) => {
      this.subscription = this.optionMonitorService
        .getData(
          this.tabType === 'instrument' ? this.instrument.stockId : this.instrument.underlyingId,
          this.tabType === 'instrument' ? type : type.toUpperCase(),
          this._getPrefix(),
          this.mode,
          this.tabType
        )
        .pipe(timeout(30000))
        .subscribe(data => {
          if (data.body) {
            data = data.body;
          }
          resolve(data);
        });
    });
  }

  private _fillData(data) {
    this.tableData = this._collectTableData(data);
    this.tableDataSize = Object.values(this.tableData).length;
    this.periods = Array.from(Object.keys(this.tableData));
    if (this.type === 'moneyness') this.values = this._getAllowableValues(this.tableData);

    this.surfaceData.x = [];
    this.surfaceData.y = [];
    this.surfaceData.z = [];

    for (const period of this.periods) {
      const dt = this.tableData[period];
      this.surfaceData.x.push(Array(dt.length).fill(period));
      this.surfaceData.y.push(dt.map(d => d.value));
      this.surfaceData.z.push(dt.map(d => d.iv));
    }
  }

  private _collectTableData(data) {
    const tbData = {};

    for (const dt of data) {
      const period = dt.period;
      const iv = dt.iv;
      const value = dt.value;

      if (period in tbData) {
        tbData[period].push({value: value, iv: iv});
      } else {
        tbData[period] = [{value: value, iv: iv}];
      }
    }

    return this._swapZeroOtm(this._discardRedundantValues(tbData));
  }

  private _swapZeroOtm(tableData) {
    for (const period of Object.keys(tableData)) {
      for (let i = 1; i < tableData[period].length; i++) {
        const value = tableData[period][i].value;
        const prevValue = tableData[period][i - 1].value;
        if (value === 0 && prevValue !== 0) {
          const tmp = tableData[period][i].iv;
          tableData[period][i].iv = tableData[period][i + 1].iv;
          tableData[period][i + 1].iv = tmp;
        }
      }
    }

    return tableData;
  }

  private _discardRedundantValues(tableData) {
    const allowableValues = this._getAllowableValues(tableData);

    for (const period of Object.keys(tableData)) {
      const data = tableData[period];
      const dataInValuesRange = [];

      for (const dt of data) {
        if (allowableValues.includes(dt['value'])) {
          dataInValuesRange.push(dt);
        }
      }

      tableData[period] = dataInValuesRange;
    }

    return tableData;
  }

  private _getAllowableValues(tableData) {
    const periodFewerValues = this._getPeriodFewerValues(tableData);
    return tableData[periodFewerValues].map(data => data['value']);
  }

  private _getPeriodFewerValues(tableData) {
    let minSize = Number.MAX_SAFE_INTEGER;
    let periodOfMinSize = null;

    for (const period of Object.keys(tableData)) {
      const size = tableData[period].length;
      if (minSize > size) {
        minSize = size;
        periodOfMinSize = period;
      }
    }
    return periodOfMinSize;
  }

  private _getPrefix() {
    if (this.tabType === 'future') return '';
    return (this.isEOD === true || (this.instrument && this.instrument.regionId !== 1)) ? '' : 'Rt';
  }
}
